import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import api from '../../../../../../api';
import { sendDemoRequest } from '../../../../../../api/custom/GetADemo';
import BookConsultation from '../../../../../components/BookConsultation';
import Button from '../../../../../components/Button';
import { useAppContext } from '../../../../../hooks/useAppContext';
import { initHubspot } from '../../../../../services/Hubspot/hubspot';
import { loadIntercom } from '../../../../../services/Intercom';
import loadScript from '../../../../../services/loadScript';
import styles from '../styles.m.less';

export type DemoTab = 'demo' | 'tutorial' | 'consultation' | null;

interface Props {
    tab: DemoTab;
    close: () => void;
}

const GetADemoModalTabs = ({ tab, close }: Props) => {
    const { user } = useAppContext();
    const [showHubspotForm, setShowHubspotForm] = useState(true);
    const [showConfirmation, setShowConfirmation] = useState(false);

    const loadHubspotForm = useCallback(async () => {
        await loadScript('https://js.hsforms.net/forms/v2.js', 'hubspot_demo');
        initHubspot(user, setShowConfirmation);
    }, [user]);

    const loadScripts = useCallback(async () => {
        // This onboardingSettings check is here to handle old trial companies from before May 2024,
        // when the OnboardingSettings form was introduced.
        const onboardingSettings = await api.onboardingSettings.getOnboardingSettings();

        if (!onboardingSettings) {
            await loadHubspotForm();
        } else if (onboardingSettings.requestedDemo) {
            setShowConfirmation(true);
        } else {
            setShowHubspotForm(false);
        }
    }, [loadHubspotForm]);

    const sendDemoRequestHandler = async () => {
        const result = await sendDemoRequest();
        if (!result) {
            await loadHubspotForm();
            setShowHubspotForm(true);
        }

        setShowConfirmation(!!result);
    };

    useEffect(() => {
        loadScripts();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (tab === 'tutorial') {
            async function startTutorialTour() {
                const intercom = await loadIntercom();
                close();
                intercom('startTour', 578915);
            }
            startTutorialTour();
        }
    }, [tab, close]);

    const renderDemoTabContent = () => {
        if (showConfirmation) {
            return (
                <div>
                    <h2>Demo requested</h2>
                    <p>
                        Thank you for requesting a Small Improvements demo. Our customer team will contact you shortly.
                    </p>
                </div>
            );
        }

        return showHubspotForm ? (
            <>
                <h2>Request a Demo</h2>
                <div id="hubspotDemoForm"></div>
            </>
        ) : (
            <>
                <h2>Request a Demo</h2>
                <p>In your customized demonstration, we'll:</p>
                <ul>
                    <li>Discuss your performance management needs</li>
                    <li>Provide a personalized walkthrough with tailored recommendations</li>
                    <li>Follow up with specific content and additional walkthroughs</li>
                </ul>
                <p>Click the button below to confirm the request:</p>
                <Button onClick={sendDemoRequestHandler} kind="PRIMARY">
                    Request a demo
                </Button>
            </>
        );
    };

    return (
        <div className={styles.tabsContainer}>
            <div className={styles.hubspotContainer}>
                <div className={classNames(styles.hide, { [styles.show]: tab === 'demo' })}>
                    {renderDemoTabContent()}
                </div>

                <div className={classNames(styles.hide, { [styles.show]: tab === 'consultation' })}>
                    {user.company.server !== 'prod-eu' && <BookConsultation />}
                </div>

                <div className={classNames(styles.hide, { [styles.show]: tab === 'tutorial' })}></div>
            </div>
        </div>
    );
};

export default GetADemoModalTabs;
